import _ from 'lodash';
import React, {FunctionComponent} from 'react';
import {
    Button as MuiButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@material-ui/core';
import {CatalogProductStatus, ProductInCatalog} from '@treez/commons/sharedTypings/product';
import {NumberInput, NumericalUnit} from '@treez-ui/core/forms';
import Checkbox from '@treez-ui/core/forms/Checkbox';
import {
    CatalogProductInformation,
    ProductsPresenceAcrossStores,
    ProductStorePresenceAttributes,
} from '~/utils/ProductAPI';
import {StoreAttributes} from '~/lib/directory';
import {formatCurrency} from '~/lib/format';
import {TreezColor, treezColorMap} from '~/styles/constants';
import PriceTierLabel from '~/modules/widgets/PriceTierLabel';

interface Props {
    catalogProductInformation: CatalogProductInformation;
    productsToMerge: ProductInCatalog[];
    stores: StoreAttributes[];
    storePresence: ProductsPresenceAcrossStores;
    updatePrice: (catalogId: number, price: string | undefined) => void;
    updatePriceTierId: (catalogId: number, priceTierId: number) => void;
    updateStatus: (catalogId: number, isActive: boolean) => void;
}

const {ACTIVE} = CatalogProductStatus;

export const MergeStoreMatrix: FunctionComponent<Props> = (props: Props) => {
    const {
        stores,
        storePresence,
        catalogProductInformation,
        updatePrice,
        updatePriceTierId,
        updateStatus,
        productsToMerge,
    } = props;

    const getStorePresencePrice = (
        productStorePresence: ProductStorePresenceAttributes[],
        catalogId: number
    ) => {
        const idx = _.findIndex(
            productStorePresence,
            (sp: ProductStorePresenceAttributes) => sp.id === catalogId
        );

        if (idx > -1) {
            return productStorePresence[idx].price;
        }

        return undefined;
    };

    const getStorePresencePriceTier = (
        productStorePresence: ProductStorePresenceAttributes[],
        catalogId: number
    ) => {
        const idx = _.findIndex(
            productStorePresence,
            (sp: ProductStorePresenceAttributes) => sp.id === catalogId
        );

        if (idx > -1) {
            return productStorePresence[idx].priceTierId;
        }

        return undefined;
    };

    const getCurrentStorePrice = (store: StoreAttributes) => {
        return _.get(catalogProductInformation, `[${store.catalogId}].price`) as string;
    };

    const getCurrentStorePriceTier = (store: StoreAttributes) => {
        return _.get(catalogProductInformation, `[${store.catalogId}].priceTierId`) as number;
    };

    const isCurrentStoreStatusActive = (store: StoreAttributes) => {
        return getCurrentStoreStatus(store) === ACTIVE;
    };

    const getStorePresenceStatus = (
        productStorePresence: ProductStorePresenceAttributes[],
        catalogId: number
    ) => {
        const idx = _.findIndex(
            productStorePresence,
            (sp: ProductStorePresenceAttributes) => sp.id === catalogId
        );

        if (idx > -1) {
            return productStorePresence[idx].status;
        }

        return undefined;
    };

    const getCurrentStoreStatus = (store: StoreAttributes) => {
        return _.get(
            catalogProductInformation,
            `[${store.catalogId}].status`
        ) as CatalogProductStatus;
    };

    const useStyles = makeStyles({
        matrixTableHeader: {
            height: '140px',
            maxHeight: '140px',
            maxWidth: '120px',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            transform: 'translate(60px, -3px) rotate(315deg)',
        },
        matrixViewCell: {
            width: '120px',
            maxWidth: '120px',
            minHeight: '42px',
            height: '42px',
            borderLeft: `1px solid ${treezColorMap[TreezColor.GREY_LIGHT]}`,
        },
        rotatedHeaderContent: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            borderBottom: `1px solid ${treezColorMap[TreezColor.GREY_LIGHT]}`,
        },
        rotatedStoreName: {
            transform: 'translate(-45px, 10px)',
        },
    });

    const {matrixTableHeader, matrixViewCell, rotatedHeaderContent, rotatedStoreName} = useStyles();

    return (
        <div className="store-matrix-container catalog-assignment-matrix-container">
            <Table className="store-matrix-table">
                <TableHead>
                    <TableRow>
                        {_.map(stores, ({name}, idx) => {
                            return (
                                <TableCell className={matrixTableHeader} key={`${name}-${idx}`}>
                                    <div className={rotatedHeaderContent}>
                                        <div className={rotatedStoreName}>{name}</div>
                                    </div>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody className="catalog-assignment-store-matrix-body">
                    <TableRow key="merged-product-store-presence">
                        {_.map(stores, (store, idx) => {
                            const isTierPricing = Boolean(
                                catalogProductInformation[store.catalogId!]?.priceTierId
                            );

                            return (
                                <TableCell key={`${store.name}-${idx}`} className={matrixViewCell}>
                                    <div className="matrix-view-cell-content-container catalog-assignment-store-input-values">
                                        <div className="catalog-assignment-input">
                                            {isTierPricing ? (
                                                <PriceTierLabel
                                                    tierId={getCurrentStorePriceTier(store)}
                                                />
                                            ) : (
                                                <NumberInput
                                                    allowDecimals
                                                    decimalPlaces={2}
                                                    onChange={(price) =>
                                                        updatePrice(store.catalogId!, price)
                                                    }
                                                    units={NumericalUnit.DOLLARS}
                                                    value={getCurrentStorePrice(store)}
                                                />
                                            )}
                                        </div>
                                        <div className="store-status-checkbox">
                                            <Checkbox
                                                checked={isCurrentStoreStatusActive(store)}
                                                onChange={() =>
                                                    updateStatus(
                                                        store.catalogId!,
                                                        !isCurrentStoreStatusActive(store)
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                    <TableRow
                        className="original-products-label-table-rows"
                        key="store-presence-original-products-row"
                    >
                        {_.map(stores, (store, idx) => (
                            <TableCell
                                key={`${store.catalogId}-${idx}`}
                                className={matrixViewCell}
                            />
                        ))}
                    </TableRow>
                    {_.map(productsToMerge, (product, idx) => {
                        const productStorePresence = storePresence[product.id];
                        return (
                            <TableRow key={`${product.id}-${idx}-store-presence`}>
                                {_.map(stores, (store, idx) => {
                                    const priceTier = getStorePresencePriceTier(
                                        productStorePresence,
                                        store.catalogId!
                                    );

                                    const isTierPricing = Boolean(priceTier);

                                    const price = getStorePresencePrice(
                                        productStorePresence,
                                        store.catalogId!
                                    );
                                    const isActive =
                                        getStorePresenceStatus(
                                            productStorePresence,
                                            store.catalogId!
                                        ) === ACTIVE;
                                    if (price === undefined || price === null) {
                                        return (
                                            <TableCell
                                                key={`${product.id}-${store.catalogId}-${idx}`}
                                                className={matrixViewCell}
                                            />
                                        );
                                    }
                                    let selected = false;

                                    if (isTierPricing) {
                                        selected = getCurrentStorePriceTier(store) === priceTier;
                                    } else {
                                        selected =
                                            formatCurrency(getCurrentStorePrice(store)) ===
                                            formatCurrency(price);
                                    }

                                    return (
                                        <TableCell
                                            key={`${product.id}-${store.catalogId}-${idx}`}
                                            onClick={() => {
                                                if (isTierPricing) {
                                                    updatePriceTierId(
                                                        store.catalogId!,
                                                        priceTier as number
                                                    );
                                                } else {
                                                    updatePrice(
                                                        store.catalogId!,
                                                        formatCurrency(price)
                                                    );
                                                }
                                            }}
                                            className={matrixViewCell}
                                        >
                                            <MuiButton className="catalog-assignment-button">
                                                <div
                                                    className={`catalog-assignment-price ${
                                                        selected ? 'selected' : ''
                                                    }`}
                                                >
                                                    {isTierPricing ? (
                                                        <PriceTierLabel tierId={priceTier!} />
                                                    ) : (
                                                        formatCurrency(price)
                                                    )}
                                                </div>
                                                <Checkbox
                                                    checked={isActive}
                                                    disabled
                                                    onChange={() => {}}
                                                />
                                            </MuiButton>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

export default MergeStoreMatrix;
